import React from 'react';
import { CheckCircleIcon, AlertCircleIcon, BuildingIcon, FileTextIcon, UserIcon, BookOpenIcon, CalendarIcon, CreditCardIcon } from 'lucide-react';
interface Step4ReviewProps {
  formData: any;
  updateFormData: (data: any) => void;
  onSubmit: (status: 'Draft' | 'Pending MoHESR' | 'Active') => void;
}
const Step4Review: React.FC<Step4ReviewProps> = ({
  formData,
  updateFormData,
  onSubmit
}) => {
  // Helper function to render coverage types in a readable format
  const renderCoverageTypes = () => {
    const coverageLabels: Record<string, string> = {
      tuition: 'Tuition Fees',
      stipend: 'Monthly Stipend',
      travel: 'Travel Allowance',
      books: 'Books & Materials',
      housing: 'Housing',
      other: 'Other Expenses'
    };
    return formData.coverageTypes.map((type: string) => coverageLabels[type] || type).join(', ');
  };
  return <div data-id="element-1062">
      <h2 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-1063">
        Review & Submit
      </h2>
      <p className="text-gray-600 mb-6" data-id="element-1064">
        Review the scholarship details before submitting to MoHESR for approval.
      </p>
      <div className="space-y-6" data-id="element-1065">
        {/* Scholarship Basics Review */}
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1066">
          <div className="flex items-center justify-between bg-gray-50 px-4 py-3 border-b border-gray-200" data-id="element-1067">
            <div className="flex items-center" data-id="element-1068">
              <FileTextIcon className="w-5 h-5 text-gray-500 mr-2" data-id="element-1069" />
              <h3 className="text-lg font-medium text-gray-800" data-id="element-1070">
                Scholarship Basics
              </h3>
            </div>
          </div>
          <div className="p-4" data-id="element-1071">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1072">
              <div data-id="element-1073">
                <p className="text-sm text-gray-500" data-id="element-1074">Scholarship Title</p>
                <p className="font-medium" data-id="element-1075">
                  {formData.scholarshipTitle || 'Not specified'}
                </p>
              </div>
              <div data-id="element-1076">
                <p className="text-sm text-gray-500" data-id="element-1077">Seats Available</p>
                <p className="font-medium" data-id="element-1078">
                  {formData.seatsAvailable || 'Not specified'}
                </p>
              </div>
              <div data-id="element-1079">
                <p className="text-sm text-gray-500" data-id="element-1080">Duration</p>
                <p className="font-medium" data-id="element-1081">
                  {formData.durationYears ? `${formData.durationYears} years` : 'Not specified'}
                </p>
              </div>
              <div data-id="element-1082">
                <p className="text-sm text-gray-500" data-id="element-1083">Coverage</p>
                <p className="font-medium" data-id="element-1084">
                  {formData.coverageTypes?.length > 0 ? renderCoverageTypes() : 'None specified'}
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Programs & Universities Review */}
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1085">
          <div className="flex items-center justify-between bg-gray-50 px-4 py-3 border-b border-gray-200" data-id="element-1086">
            <div className="flex items-center" data-id="element-1087">
              <BuildingIcon className="w-5 h-5 text-gray-500 mr-2" data-id="element-1088" />
              <h3 className="text-lg font-medium text-gray-800" data-id="element-1089">
                Programs & Universities
              </h3>
            </div>
          </div>
          <div className="p-4" data-id="element-1090">
            <div className="mb-4" data-id="element-1091">
              <p className="text-sm text-gray-500" data-id="element-1092">Program Name</p>
              <p className="font-medium" data-id="element-1093">
                {formData.programName || 'Not specified'}
              </p>
            </div>
            <div data-id="element-1094">
              <p className="text-sm text-gray-500" data-id="element-1095">Preferred Universities</p>
              {formData.preferredUniversities?.length > 0 ? <div className="mt-2 flex flex-wrap gap-2" data-id="element-1096">
                  {formData.preferredUniversities.map((university: any) => <div key={university.id} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm flex items-center" data-id="element-1097">
                      <BuildingIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-1098" />
                      {university.name}
                    </div>)}
                </div> : <p className="font-medium" data-id="element-1099">All eligible universities</p>}
            </div>
          </div>
        </div>
        {/* Application Form Review */}
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1100">
          <div className="flex items-center justify-between bg-gray-50 px-4 py-3 border-b border-gray-200" data-id="element-1101">
            <div className="flex items-center" data-id="element-1102">
              <UserIcon className="w-5 h-5 text-gray-500 mr-2" data-id="element-1103" />
              <h3 className="text-lg font-medium text-gray-800" data-id="element-1104">
                Application Form
              </h3>
            </div>
          </div>
          <div className="p-4" data-id="element-1105">
            <div className="mb-4" data-id="element-1106">
              <p className="text-sm text-gray-500" data-id="element-1107">Eligibility Preset</p>
              <p className="font-medium capitalize" data-id="element-1108">
                {formData.eligibilityPreset || 'Flexible'}
              </p>
            </div>
            <div data-id="element-1109">
              <p className="text-sm text-gray-500 mb-2" data-id="element-1110">Form Fields</p>
              {formData.formFields?.length > 0 ? <div className="space-y-2" data-id="element-1111">
                  {formData.formFields.map((field: any, index: number) => <div key={index} className="flex items-center" data-id="element-1112">
                      <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" data-id="element-1113" />
                      <span className="text-gray-700" data-id="element-1114">
                        {field.label}{' '}
                        {field.required && <span className="text-xs text-red-500" data-id="element-1115">
                            (Required)
                          </span>}
                      </span>
                    </div>)}
                </div> : <p className="text-gray-500 italic" data-id="element-1116">No form fields added</p>}
            </div>
          </div>
        </div>
        {/* Submission Notice */}
        <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4" data-id="element-1117">
          <div className="flex items-start" data-id="element-1118">
            <AlertCircleIcon className="w-5 h-5 text-yellow-500 mr-3 mt-0.5" data-id="element-1119" />
            <div data-id="element-1120">
              <h3 className="text-sm font-medium text-yellow-800 mb-1" data-id="element-1121">
                Approval Required
              </h3>
              <p className="text-sm text-yellow-700" data-id="element-1122">
                After submission, your scholarship will be in "Pending MoHESR
                Approval" status. The Ministry will review your application and
                may contact you for additional information before approving it.
              </p>
            </div>
          </div>
        </div>
        {/* Declaration */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1123">
          <div className="flex items-start" data-id="element-1124">
            <div className="flex items-center h-5" data-id="element-1125">
              <input id="declaration" name="declaration" type="checkbox" className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500" required data-id="element-1126" />
            </div>
            <label htmlFor="declaration" className="ml-3 text-sm text-gray-700" data-id="element-1127">
              I confirm that the information provided for this scholarship is
              accurate. I understand that the Ministry of Higher Education and
              Scientific Research (MoHESR) will review this scholarship before
              it becomes active on the platform.
            </label>
          </div>
        </div>
      </div>
    </div>;
};
export default Step4Review;